import { useState } from "react"
import { toast } from "react-toastify"

import { Button } from "~/components/reusable/Button"
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import { Input } from "~/components/reusable/Input"
import Select from "~/components/reusable/Select"
import { trackEvent } from "~/hooks/useTrackingService"
import { userRoleOptions } from "~/models/enum"
import { UserRole } from "~/providers/userProvider"
import { api } from "~/utils/api"

interface Props {
  organizationId?: string
}

export const InviteUserDialog: React.FC<Props> = ({ organizationId }) => {
  const utils = api.useUtils()

  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  const [role, setRole] = useState<UserRole>(UserRole.USER)

  const inviteUser = api.auth0.inviteUser.useMutation({
    onError: async () => {
      toast.error("Failed to invite user")
    },
    onSuccess: async () => {
      trackEvent("User Invited", {
        Role: role,
      })
      toast.success(email + " has been invited")
      void utils.auth0.getInvitations.invalidate()
      setOpen(false)
      setEmail("")
    },
  })

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      <DialogTrigger requires="administer" asChild>
        <Button size="sm" variant="neutral">
          Invite user
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite user to Hakio</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <div className="text-sm">
            Enter the email of the user you want to invite. They will receive an email with a link
            to join your organization.
          </div>
          <div className="flex flex-row gap-4 items-end">
            <Input
              label="Email"
              containerClassName="w-full"
              type="email"
              placeholder="Enter email..."
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Select
              label="User role"
              className="min-w-[150px]"
              options={userRoleOptions}
              onChange={(option) => {
                setRole(option!.value as UserRole)
              }}
              value={userRoleOptions.find((option) => option.value === role)}
            />
          </div>
        </div>
        <DialogFooter className="flex justify-between">
          <DialogClose>
            <Button asChild variant="transparent">
              Cancel
            </Button>
          </DialogClose>
          <Button
            requires="administer"
            disabled={!email}
            loading={inviteUser.isPending}
            onClick={() => inviteUser.mutate({ email, role, organizationId })}
          >
            Invite user
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
